import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  Output,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import FileSaver from 'file-saver';
import { MenuItem, MessageService, SelectItem } from 'primeng';
import { TaskTypeHelper } from '../../../../utils/taskType.helper';
import { JobStatus } from '../../../../enums/job-status';
import { EventService, JobsService, MapService, OrderService } from '../../../../services';
import { Message } from '../../../../classes/Message';
import { JobScreenCommonService } from '../job-screen-common.service';
import { Router } from '@angular/router';
import { ITaskTrackingDTO } from '../../../../interfaces/task-tracking-info';
import { IMarkerPoint, PauseMarkerPoint } from '../../../map-control/marker.point';
import { RefreshJobEventTypeEnum } from '../../../../enums/refresh-job-event-type.enum';
import { Panel } from 'primeng/panel/panel';
import { JobDetailsChecklistSubCategory } from '../../../../interfaces/checklist-category';
import { Subscription } from 'rxjs';
import {
  TaskSupplierScheduleSource,
  TaskSupplierScheduleStore,
} from '../../../task-supplier-schedule/task-supplier-schedule-store';
import { cloneDeep } from 'lodash';
import { TaskSequenceService } from '../../../../services/task-sequence.service';
import { OrganisationTypeEnum } from '../../../../enums/organisation-types';

export interface IJobScreenOrderTasks {
  jobSupplierId: number | null;
  orderStatusId: number;
  jobStatusId: number;
  orderNumber: string;
  orderId: number;
  jobId: number;
  tasks: IJobScreenOrderTasksTaskInfo[];
}

export interface IJobScreenOrderTasksTaskInfo {
  checklistSubCategories: JobDetailsChecklistSubCategory[];
  isComplete: boolean;
  isCancelled: boolean;
  canBeRescheduled: boolean;
  requiresDelayFailureReason: boolean;
  taskTypeId: number;
  taskTypeName: string;
  distance: number;
  vehicle: IJobScreenTasksInfoVehicle;
  id: number;
  orderId: number;
  workflowUrl: string;
  pickupEarliestDateTime: string;
  pickupLatestDateTime: string;
  dropoffEarliestDateTime: string;
  dropoffLatestDateTime: string;
  estimateTime: string;
  pickupLocation: IMarkerPoint;
  dropoffLocation: IMarkerPoint;
  polyline: string;
  taskTracking: ITaskTrackingDTO[];
  currentLocation: ITaskTrackingDTO;
  pauses: PauseMarkerPoint[];
  isRescheduled: boolean;
  taskSequence?: number | null;
}

@Component({
  templateUrl: './job-screen-order-tasks.component.html',
  selector: 'job-screen-job-details',
  styleUrls: [
    './job-screen-order-tasks.component.scss',
    '../job-screen-common.scss',
    '../common-scss/job-screen-tasks-common.scss',
  ],
  providers: [TaskTypeHelper],
})
export class JobScreenOrderTasksComponent implements OnChanges, OnDestroy {
  public FullOrderUrl: string;
  public FullOrderPDFUrl: string;
  public SummaryOrderUrl: string;
  public SummaryOrderPDFUrl: string;

  public jobStatus = JobStatus;

  public showCopyTrackUrlDialog: boolean = false;
  public showCopyConfirmation: boolean = false;
  public showEditTaskDialog: boolean = false;

  pdfDownloading: boolean = false;

  TaskSupplierScheduleSource = TaskSupplierScheduleSource;

  workFlows: MenuItem[];

  @Output() editSupplierSchedule = new EventEmitter();

  @Input() hideTaskSchedule: boolean = false;

  @Input() organisationTypeId: number;
  @Input() organisationId: number;
  @Input() orderId: number;

  @ViewChild('panel') panel: Panel;

  selectedTaskId: number;

  selectedTask: IJobScreenOrderTasksTaskInfo;

  public taskSequenceEditMode: boolean = false;
  public taskSequencingEnabled: boolean = false;

  public get secondaryVehicleInfo(): string[] {
    if (!this.selectedTask) {
      return [];
    }

    const fields = [
      this.selectedTask.vehicle.color,
      this.selectedTask.vehicle.kerbWeight ? `${this.selectedTask.vehicle.kerbWeight}kg` : null,
      this.selectedTask.vehicle.fuelType,
    ];

    return fields.filter((x) => x != null && x != '');
  }

  public tasks: SelectItem[];
  public tasksCopyForSequenceEdit: SelectItem[] = [];

  public order: IJobScreenOrderTasks;

  private validStates = [
    JobStatus.Cancelled,
    JobStatus.Completed,
    JobStatus.Closed,
    JobStatus.Started,
  ];

  public showOrderTrack: boolean;
  public delayFailureDialogVisible: boolean = false;
  public rescheduleDialogVisible: boolean = false;

  public pdfLinkData = [
    {
      label: 'Full PDF',
      icon: 'fa fa-file-pdf-o pdf-icon',
      command: () => {
        this.downloadTrack(false, false);
      },
    },
    {
      label: 'Full PDF (no condition)',
      icon: 'fa fa-file-pdf-o pdf-icon',
      command: () => {
        this.downloadTrack(false, true);
      },
    },
    {
      label: 'Summary PDF',
      icon: 'fa fa-file-pdf-o pdf-icon',
      command: () => {
        this.downloadTrack(true, false);
      },
    },
  ];

  editTaskScheduleVisible: boolean;

  routeInfo: { distanceMiles: number; time: string };
  interactiveMap: boolean = false;
  staticMapSrc: string;
  mapWidth: number;

  subscriptions: Subscription[] = [];
  editVehicleVisible: boolean = false;

  protected readonly OrganisationTypeEnum = OrganisationTypeEnum;

  constructor(
    private mapService: MapService,
    private orderService: OrderService,
    private taskTypeHelper: TaskTypeHelper,
    private jobService: JobsService,
    private messageService: MessageService,
    private eventService: EventService,
    private router: Router,
    public commonService: JobScreenCommonService,
    private taskSequenceService: TaskSequenceService,
  ) {
    this.subscriptions.push(
      this.eventService.JobScreenEvent.subscribe((event) => {
        this.handleEvents(event);
      }),
    );
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes.orderId && this.orderId != null) {
      this.selectedTaskId = null;
      void this.fetchOrder();
    }
  }

  handleEvents(event: any) {
    if (event.event == 'task-changed') {
      this.selectedTaskId = event.data.taskId;
      this.updateSelectedTask();
    }

    if (event.event == 'refresh-order') {
      void this.fetchOrder();
    }

    if (event.event == 'sequence-update') {
      void this.fetchOrder();
    }
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach((x) => x?.unsubscribe());
  }

  goToSimilarJobs() {
    void this.router.navigate([`/app/similar-jobs/${this.selectedTaskId}`]);
  }

  changeSelectedTask(taskId: number) {
    this.eventService.JobScreenEvent.emit({
      event: 'task-changed',
      data: { taskId, orderId: this.selectedTask?.orderId },
      sender: 'changeSelectedTask',
    });
  }

  updateSelectedTask() {
    this.selectedTask = this.order.tasks.find((o) => o.id == this.selectedTaskId);

    if (this.selectedTask == null) {
      return;
    }

    // @ts-ignore
    const mapWidth = Math.round(this.panel.el.nativeElement.offsetWidth * 0.4);
    this.mapWidth = mapWidth;

    this.staticMapSrc = this.mapService.getStaticMapWithMarkers([this.selectedTask], {
      selectedTaskId: this.selectedTaskId,
      mapSize: `${mapWidth}x398`,
    });

    // this.taskSupplierScheduleStore = new TaskSupplierScheduleStore(
    //   this.selectedTask as ITaskSupplierScheduleStoreTask,
    //   null,
    //   [],
    // );

    this.updateRouteInfo();

    if (this.selectedTask.taskTracking.length == 0) {
      this.loadTaskTrackingForTask(this.selectedTask.id);
    }
  }

  refreshWorkflows() {
    this.workFlows = this.order.tasks.map((x) => {
      return {
        label: 'Workflow ' + x.vehicle.regNumber,
        icon: 'fa fa-external-link',
      };
    });
  }

  refreshOrder() {
    this.tasks = [];

    if (this.order) {
      this.refreshWorkflows();

      //this.showOrderTrack = (this.organisationTypeId == 1 || this.validStates.indexOf(this.job.status) > -1 || (this.job.statusId && this.validStates.indexOf(this.job.statusId) > -1)) ? true : false;
      this.order.tasks.forEach((item) => {
        this.tasks.push({ label: item.vehicle.regNumber, value: item });
      });

      this.taskSequencingEnabled = this.tasks.some((task) => task.value.taskSequence != null);

      this.setTaskOrder();
      this.selectedTask = this.order.tasks.find((o) => o.id == this.selectedTaskId);
      if (this.selectedTaskId == null) {
        this.changeSelectedTask(this.order.tasks[0].id);
      }
    }
  }

  setTaskOrder() {
    const hasSequencedTasks = this.tasks.some((task) => task.value.taskSequence != null);

    this.tasks.sort(function compare(a, b) {
      if (hasSequencedTasks) {
        return a.value.taskSequence - b.value.taskSequence;
      }

      let dateA = new Date(a.value.pickupEarliestDateTime);
      let dateB = new Date(b.value.pickupEarliestDateTime);

      return +dateA - +dateB;
    });
  }

  updateRouteInfo() {
    this.showOrderTrack =
      this.organisationTypeId == 1 ||
      (this.order.orderStatusId && this.validStates.indexOf(this.order.orderStatusId) > -1);

    this.routeInfo = { distanceMiles: null, time: null };
    const url = window.location.href;

    const urlParams = `?o=${encodeURIComponent(
      this.order.orderNumber.toString(),
    )}&r=${encodeURIComponent(this.selectedTask.vehicle.regNumber)}`;

    this.FullOrderUrl = url.substring(0, url.indexOf('/app')) + `/Track${urlParams}`;
    this.FullOrderPDFUrl = `/Track/Report-DynamicPdf2${urlParams}`;
    this.SummaryOrderPDFUrl = `/Track/Report-Summary${urlParams}`;
    this.SummaryOrderUrl = url.substring(0, url.indexOf('/app')) + `/Track/Summary${urlParams}`;

    // convert timespan string to seconds
    if (this.selectedTask.estimateTime) {
      this.routeInfo.time = this.mapService.getTravelTimeFormatted(this.selectedTask.estimateTime);
      this.routeInfo.distanceMiles = this.mapService.getMiles(this.selectedTask.distance);
    }
  }

  recordDelayFailure() {
    this.delayFailureDialogVisible = true;
  }

  showTrackURL() {
    this.showCopyTrackUrlDialog = true;
    let lbl = document.getElementById('lblMsg') as HTMLLabelElement;
    this.showCopyConfirmation = false;
  }

  copyUrl(index: number) {
    let copyUrl = document.getElementById('txtUrl' + index) as HTMLInputElement;
    this.showCopyConfirmation = true;
    copyUrl.style.display = 'block';
    copyUrl.select();
    document.execCommand('copy');
    copyUrl.style.display = 'none';
  }

  downloadTrack(summaryOnly: boolean, hideDamage: boolean = false) {
    let uri = summaryOnly ? this.SummaryOrderPDFUrl : this.FullOrderPDFUrl;
    this.pdfDownloading = true;

    uri += '&hd=' + hideDamage;

    this.jobService.downloadPDF(uri).subscribe(
      (x) => {
        FileSaver.saveAs(x);
      },
      () => {
        this.messageService.add(Message.Error('Error downloading PDF'));
        this.pdfDownloading = false;
      },
      () => {
        this.pdfDownloading = false;
      },
    );
  }

  getPDFIcon() {
    if (this.pdfDownloading) return 'fa fa-spinner fa-spin';
    else return 'fa fa-file-pdf-o pdf-icon';
  }

  visitURL(infoURL: string) {
    window.open(infoURL, '_blank');
  }

  loadTaskTrackingForTask(taskId: number): void {
    this.jobService.getTaskTrackingInfo(this.order.jobId, taskId).then((taskTrackingInfo) => {
      if (taskTrackingInfo && taskTrackingInfo.length > 0) {
        this.selectedTask = {
          ...this.selectedTask,
          taskTracking: taskTrackingInfo,
          currentLocation: taskTrackingInfo[taskTrackingInfo.length - 1],
        };
        this.updateRouteInfo();
      }
    });
  }

  async fetchOrder() {
    this.order = await this.orderService.getForComponent<IJobScreenOrderTasks>(
      this.orderId,
      'job-screen-order-tasks',
    );
    this.refreshOrder();
  }

  onEditSupplierScheduleClick() {
    this.editTaskScheduleVisible = true;
  }

  onScheduleUpdated() {
    this.eventService.JobScreenEvent.emit({
      event: 'refresh-schedule',
    });
    this.eventService.RefreshJobEvent.emit(RefreshJobEventTypeEnum.RefreshHistory);
  }

  editLocations(taskId: number) {
    //this.showEditLocationsDialog = true;
    void this.router.navigate(['/app/edit-task-location/' + taskId]);
  }

  onDelayFailureCreated() {
    this.delayFailureDialogVisible = false;
    void this.fetchOrder();
    this.eventService.DelayFailureRefreshEvent.emit();
    this.eventService.RefreshJobEvent.emit(RefreshJobEventTypeEnum.RefreshHistory);
  }

  onTaskWindowUpdated() {
    this.showEditTaskDialog = false;
    void this.fetchOrder();
    this.eventService.RefreshJobEvent.emit(RefreshJobEventTypeEnum.RefreshHistory);
    this.eventService.JobScreenEvent.emit({
      event: 'refresh-task-times',
    });
    this.eventService.JobScreenEvent.emit({
      event: 'job-details-changed',
      data: { jobId: this.order?.jobId },
      sender: 'editTaskWindows',
    });
  }

  onVehicleUpdated() {
    void this.fetchOrder();
    this.editVehicleVisible = false;
  }

  handleTaskSequenceToggle(event: any): void {
    let enabled = event.checked;

    if (enabled) {
      this.enableTaskSequencing();
    } else {
      this.disableTaskSequencing();
    }
  }

  enableTaskSequencing(): void {
    this.tasks.forEach((task, index) => {
      task.value.taskSequence = index + 1;
    });

    this.saveTaskSequence(true);

    void this.fetchOrder();
  }

  disableTaskSequencing(): void {
    this.tasks.forEach((task, index) => {
      task.value.taskSequence = null;
    });

    this.saveTaskSequence(true);

    void this.fetchOrder();
  }

  editTaskSequence(): void {
    this.taskSequenceEditMode = true;
    this.tasksCopyForSequenceEdit = cloneDeep(this.tasks);
  }

  moveTaskSequenceDown(task: SelectItem) {
    const index = this.tasksCopyForSequenceEdit.indexOf(task);
    if (index !== -1 && index < this.tasksCopyForSequenceEdit.length - 1) {
      [this.tasksCopyForSequenceEdit[index], this.tasksCopyForSequenceEdit[index + 1]] = [
        this.tasksCopyForSequenceEdit[index + 1],
        this.tasksCopyForSequenceEdit[index],
      ];
      this.updateTaskSequenceIds(this.tasksCopyForSequenceEdit);
    }
  }

  moveTaskSequenceUp(task: SelectItem) {
    const index = this.tasksCopyForSequenceEdit.indexOf(task);
    if (index > 0) {
      [this.tasksCopyForSequenceEdit[index], this.tasksCopyForSequenceEdit[index - 1]] = [
        this.tasksCopyForSequenceEdit[index - 1],
        this.tasksCopyForSequenceEdit[index],
      ];
      this.updateTaskSequenceIds(this.tasksCopyForSequenceEdit);
    }
  }

  updateTaskSequenceIds(tasks: SelectItem[]): void {
    tasks.forEach((task, i) => {
      task.value.taskSequence = i + 1;
    });
  }

  cancelTaskSequenceEdit(): void {
    this.taskSequenceEditMode = false;
  }

  saveTaskSequence(bypassClone: boolean = false): void {
    if (!bypassClone) {
      this.tasks = cloneDeep(this.tasksCopyForSequenceEdit);
    }

    this.taskSequenceEditMode = false;

    this.taskSequenceService
      .updateTaskSequenceFromJobScreen(
        this.tasks.map((task) => task.value),
        this.orderId,
      )
      .then(() => {
        this.messageService.add(Message.Success('Task sequence updated successfully.'));
        this.eventService.JobScreenEvent.emit({
          event: 'sequence-update',
          sender: 'task-sequence',
        });
      })
      .catch(() => {
        this.messageService.add(
          Message.Error('An error occurred when attempting to update this task sequence'),
        );
      });
  }

  protected readonly TaskSupplierScheduleStore = TaskSupplierScheduleStore;
}
